import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, PasswordInput, Checkbox } from '@medikei/ui';
import { AuthLayout, AuthHeader, FormFooter, GoogleLoginButton } from '@/components/auth';
import { Captcha } from '@/components/common/Captcha';
import { authApi, httpClient, AuthApiError } from '@/api/http-client';
import { useAuth } from '@/hooks';
import {
  AlertCircle,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import { cn } from '@/utils/cn';

const loginSchema = z.object({
  email: z.string().min(1, 'Email é obrigatório').email('Email inválido'),
  password: z.string().min(1, 'Senha é obrigatória'),
});

type LoginForm = z.infer<typeof loginSchema>;

type LoginErrorType =
  | 'invalid_credentials'
  | 'account_locked'
  | 'rate_limit'
  | 'mfa_required';

interface LoginError {
  type: LoginErrorType;
  message: string;
  retryAfter?: number;
}

export function LoginPage() {
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect') || searchParams.get('returnUrl');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (formData: LoginForm) => {
    setError(null);

    try {
      const response = await authApi.login(formData.email, formData.password, captchaToken);

      if (response.requiresTwoFactor && response.pendingSessionId) {
        navigate('/mfa', {
          state: {
            pendingSessionId: response.pendingSessionId,
            userId: response.user?.id,
            email: formData.email,
          },
        });
        return;
      }

      httpClient.saveAccessToken(response.accessToken);
      await login(formData.email, formData.password, response);
      const getRedirectPath = (role?: string) => {
        if (redirectParam && redirectParam.startsWith('/')) {
          return redirectParam;
        }
        switch (role) {
          case 'ADMIN':
            return '/admin';
          case 'DOCTOR':
            return '/medico/dashboard';
          case 'PATIENT':
          case 'SUPPORT':
            return '/dashboard';
          default:
            return '/dashboard';
        }
      };
      const redirectPath = getRedirectPath(response.user?.role);
      navigate(redirectPath, { replace: true });
    } catch (err) {
      const error = err as Error;
      if (error instanceof AuthApiError) {
        switch (error.code) {
          case 'ACCOUNT_LOCKED':
          case 'ACCOUNT_BLOCKED':
            setError({
              type: 'account_locked',
              message:
                error.message ||
                'Sua conta foi temporariamente bloqueada por segurança. Entre em contato com o suporte.',
            });
            break;
          case 'RATE_LIMIT':
            setError({
              type: 'rate_limit',
              message:
                error.message ||
                'Muitas tentativas de acesso. Aguarde alguns instantes antes de tentar novamente.',
              retryAfter: error.retryAfter,
            });
            break;
          case 'INVALID_CREDENTIALS':
            setError({
              type: 'invalid_credentials',
              message:
                error.message && !error.message.includes('Invalid credentials')
                  ? error.message
                  : 'E-mail ou senha incorretos. Verifique seus dados de acesso.',
            });
            break;
          case 'MFA_REQUIRED':
            setError({
              type: 'mfa_required',
              message: error.message || 'Autenticação em duas etapas necessária.',
            });
            break;
          default:
            setError({
              type: 'invalid_credentials',
              message:
                error.message && !error.message.includes('Invalid credentials')
                  ? error.message
                  : 'E-mail ou senha incorretos. Verifique seus dados de acesso.',
            });
        }
      } else {
        const rawMsg = error?.message || '';
        setError({
          type: 'invalid_credentials',
          message:
            rawMsg.includes('Invalid credentials') || rawMsg.includes('Unauthorized')
              ? 'E-mail ou senha incorretos. Verifique seus dados de acesso.'
              : rawMsg || 'Falha ao acessar a conta. Verifique seus dados e tente novamente.',
        });
      }
    } finally {
    }
  };

  const getErrorIcon = () => {
    switch (error?.type) {
      case 'account_locked':
        return ShieldCheck;
      case 'rate_limit':
        return Clock;
      case 'mfa_required':
        return ShieldCheck;
      default:
        return AlertCircle;
    }
  };

  const ErrorIcon = getErrorIcon();

  return (
    <AuthLayout>
      <AuthHeader
        title="Entrar"
        description="Acesse sua conta na plataforma de saúde"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {error && (
          <div
            className={cn(
              'flex items-start gap-3 p-4 rounded-lg border text-sm',
              error.type === 'rate_limit'
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-red-50 border-red-200'
            )}
            role="alert"
          >
            <ErrorIcon
              size={18}
              className={cn(
                'flex-shrink-0 mt-0.5',
                error.type === 'rate_limit' ? 'text-yellow-500' : 'text-red-500'
              )}
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 break-words">
                {error.message}
              </p>
              {error.type === 'rate_limit' && error.retryAfter && (
                <p className="text-xs text-gray-500 mt-1">
                  Tente novamente em {error.retryAfter} segundos
                </p>
              )}
            </div>
          </div>
        )}

        {error?.type === 'mfa_required' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <ShieldCheck size={18} />
              <span className="text-sm font-medium">
                Verificação em duas etapas necessária
              </span>
            </div>
            <p className="text-xs text-blue-600">
              Você será redirecionado para a página de verificação
            </p>
          </div>
        )}

        <Input
          label="Email"
          type="email"
          placeholder="seu@email.com"
          autoComplete="email"
          autoFocus
          {...register('email')}
          error={errors.email?.message}
        />

        <PasswordInput
          label="Senha"
          placeholder="••••••••"
          autoComplete="current-password"
          {...register('password')}
          error={errors.password?.message}
        />

        <div className="flex items-center justify-between">
          <Checkbox
            label="Lembrar-me"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Esqueci minha senha
          </Link>
        </div>

        <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />

        <Button
          type="submit"
          className="w-full rounded-xl"
          disabled={isSubmitting || error?.type === 'rate_limit'}
          loading={isSubmitting}
        >
          {isSubmitting ? 'Entrando...' : 'Entrar'}
        </Button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-200 dark:border-neutral-700" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground font-medium">ou</span>
          </div>
        </div>

        <GoogleLoginButton mode="login" onError={(msg) => setError({ type: 'invalid_credentials', message: msg })} />

        <div className="pt-2 text-center">
          <p className="text-sm text-gray-500 dark:text-neutral-400">
            Não tem uma conta?{' '}
            <Link
              to={redirectParam ? `/register?redirect=${encodeURIComponent(redirectParam)}` : '/register'}
              className="font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Cadastre-se
            </Link>
          </p>
        </div>
      </form>

      <div className="mt-6 pt-4 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-500" />
        <span>Ambiente seguro & conforme LGPD</span>
      </div>

      <FormFooter>
        Ao entrar, você concorda com nossos{' '}
        <Link to="/terms" className="underline hover:text-gray-900 dark:hover:text-white transition-colors">
          Termos de Uso
        </Link>{' '}
        e{' '}
        <Link to="/privacy" className="underline hover:text-gray-900 dark:hover:text-white transition-colors">
          Política de Privacidade
        </Link>
      </FormFooter>
    </AuthLayout>
  );
}